import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Search, RefreshCw, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/admin-api';
import { formatDateTime, formatRelative, statusBadgeClass } from './formatters';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'converted', label: 'Converted' },
  { value: 'archived', label: 'Archived' },
];

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const response = await adminApi.get('/contacts', { params });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (_e) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  return (
    <AdminLayout
      title="Contact Inquiries"
      subtitle={`${total} contact submission${total === 1 ? '' : 's'} on file.`}
    >
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <form onSubmit={handleSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name, email, company…"
            className="pl-9"
            data-testid="admin-contacts-search"
          />
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[180px]" data-testid="admin-contacts-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchData} className="gap-1.5" data-testid="admin-contacts-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
      </div>

      <Card className="editorial-card p-0">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-12 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-contacts-empty">
              <p className="text-muted-foreground">No contact submissions match the current filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-contacts-table">
                <thead className="bg-[hsl(var(--bone-2))] text-xs uppercase tracking-wider text-muted-foreground font-mono">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Contact</th>
                    <th className="text-left px-4 py-3 font-medium">Company</th>
                    <th className="text-left px-4 py-3 font-medium">Project Type</th>
                    <th className="text-left px-4 py-3 font-medium">Submitted</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item._id}
                      className="border-t border-bone-2 hover:bg-[hsl(var(--bone-2)/0.6)] transition-colors"
                      data-testid={`admin-contact-row-${item._id}`}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/admin/contacts/${item._id}`} className="block">
                          <p className="font-display font-semibold text-foreground">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.email}</p>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-foreground/85">{item.company || '—'}</td>
                      <td className="px-4 py-3 text-foreground/85">{item.project_type || '—'}</td>
                      <td className="px-4 py-3 text-foreground/85" title={formatDateTime(item.submitted_at)}>
                        {formatRelative(item.submitted_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={`font-mono text-[10px] tracking-[0.18em] uppercase ${statusBadgeClass(item.status)}`}>
                          {item.status || 'new'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/contacts/${item._id}`}
                          className="inline-flex items-center text-primary hover:underline text-xs gap-1"
                          data-testid={`admin-contact-open-${item._id}`}
                        >
                          Open
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
